"use client";

import messages from "@/i18n/messages/en.json";

import { useState } from "react";
import { Button } from "@artha/ui";
import { Upload } from "lucide-react";
import { CreatorFloatingActions } from "@/components/layout/CreatorFloatingActions";
import {
  CREATOR_FLOATING_PRIMARY_ACTION_CLASS_NAME,
  CREATOR_FLOATING_SECONDARY_ACTION_CLASS_NAME,
} from "@/components/layout/creatorDetail.constants";
import { CandidateProfileContactEditorSchema } from "@artha/api-contracts";
import { ProfileBasicsSection } from "./sections/ProfileBasicsSection";
import { ProfileChipSection } from "./sections/ProfileChipSection";
import { ProfileHistorySection } from "./sections/ProfileHistorySection";
import {
  PROFILE_CARD_CHIP_KINDS,
  PROFILE_CARD_HISTORY_KINDS,
} from "./profileCards.constants";
import type {
  CandidateProfileCardsProps,
  ProfileCardEditor,
} from "./profileCards.types";

export function CandidateProfileCards({
  profile,
  contactFooter,
  readOnly,
  disabled,
  submitting,
  submitDisabled,
  submitLabel,
  onChange,
  onSubmit,
  onReupload,
  emailVerified,
  phoneVerified,
  sendingEmail,
  emailResendSeconds,
  onVerifyEmail,
  onVerifyPhone,
  editingContact,
  onContactEditorClose,
}: CandidateProfileCardsProps) {
  const [activeEditor, setEditor] = useState<ProfileCardEditor>(null);
  const editor: ProfileCardEditor = editingContact ? { section: "basics" } : activeEditor;
  const [error, setError] = useState("");
  const close = () => {
    setEditor(null);
    onContactEditorClose?.();
  };
  const save = (next: typeof profile) => {
    onChange?.(next);
    setError("");
    close();
  };
  return (
    <div className="space-y-5">
      {error && !editor ? (
        <p role="alert" className="text-sm text-[#B42318]">
          {error}
        </p>
      ) : null}
      <ProfileBasicsSection
        readOnly={readOnly}
        profile={profile}
        contactFooter={contactFooter}
        emailVerified={emailVerified}
        phoneVerified={phoneVerified}
        sendingEmail={sendingEmail}
        emailResendSeconds={emailResendSeconds}
        onVerifyEmail={onVerifyEmail}
        onVerifyPhone={onVerifyPhone}
        editing={editor?.section === "basics"}
        editingContact={editingContact}
        error={error}
        disabled={disabled || (editor !== null && editor.section !== "basics")}
        onEdit={() => setEditor({ section: "basics" })}
        onCancel={close}
        onSave={save}
      />
      {PROFILE_CARD_HISTORY_KINDS.map((kind) => (
        <ProfileHistorySection
          readOnly={readOnly}
          key={kind}
          kind={kind}
          entries={profile[kind] || []}
          editor={editor}
          disabled={disabled}
          onEdit={(index) => setEditor({ section: kind, index })}
          onCancel={close}
          onChange={(entries) => save({ ...profile, [kind]: entries })}
        />
      ))}
      {PROFILE_CARD_CHIP_KINDS.map((kind) => (
        <ProfileChipSection
          readOnly={readOnly}
          key={kind}
          kind={kind}
          values={profile[kind]}
          editing={editor?.section === kind}
          error={error}
          disabled={disabled || (editor !== null && editor.section !== kind)}
          onEdit={() => setEditor({ section: kind })}
          onCancel={close}
          onSave={(values) => save({ ...profile, [kind]: values })}
        />
      ))}
      {!readOnly ? <CreatorFloatingActions role="group" aria-label={messages.talentNetworkUi.profile_actions}>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={CREATOR_FLOATING_SECONDARY_ACTION_CLASS_NAME}
          aria-label={messages.talentNetworkUi.upload_a_different_resume}
          title={messages.talentNetworkUi.upload_a_different_resume}
          disabled={disabled || editor !== null}
          onClick={onReupload}
        >
          <Upload aria-hidden="true" className="h-5 w-5 text-[#101828]" />
        </Button>
        <Button
          type="button"
          disabled={disabled || submitDisabled || editor !== null}
          className={CREATOR_FLOATING_PRIMARY_ACTION_CLASS_NAME}
          onClick={() => {
            if (!CandidateProfileContactEditorSchema.safeParse(profile).success) {
              setError(messages.talentNetworkUi.add_your_name_and_a_valid_email_before_continuing);
              setEditor({ section: "basics" });
              return;
            }
            if (
              !profile.titles.length &&
              !profile.skills.length &&
              !profile.keywords.length
            ) {
              setError(
                messages.talentNetworkUi.add_at_least_one_skill_or_job_title_to,
              );
              setEditor({ section: "skills" });
              return;
            }
            onSubmit?.();
          }}
        >
          {submitting ? messages.talentNetworkUi.saving_profile : submitLabel}
        </Button>
      </CreatorFloatingActions> : null}
    </div>
  );
}
